import { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  Button,
  Backdrop,
  CircularProgress,
  Autocomplete,
  TextField,
} from "@mui/material";
import { DatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import dayjs from "dayjs";
import ERPGrid from "../components/ERPGrid";
import { util } from "../util/index";
import { ColDef } from "ag-grid-community";
import ERPSnakBar from "../components/ERPSnakBar";
import useLocalStorage from "../hooks/useLocalStorage";
import {
  getShipDataByClient,
  getShipDataAll,
} from "../services/shipDataService";
import { getClientForSelection } from "../services/clientService";
import download from "downloadjs";
import { ExportToExcel } from "../util/globals";

function ShippingList() {
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [fromDate, setFromDate] = useState(
    dayjs(new Date()).subtract(2, "month")
  );
  const [alert, setAlert] = useState({
    open: false,
    message: "",
    serverity: "",
  });
  const [toDate, setToDate] = useState(dayjs(new Date()));
  const gridRef = useRef<any>(null);
  const { user_type, client_id } = useLocalStorage();
  const [pinnedRows, setPinnedRows] = useState<any[]>([]);
  const [clients, setClients] = useState([]);
  const [selectedClient, setSelectedClient] = useState<any>(null);

  const colDefs: ColDef[] = [
    {
      field: "client",
      headerName: "Client",
      width: 150,
      filter: true,
      hide: user_type === "Admin" ? false : true,
    },
    {
      filter: true,
      field: "vendor",
      headerName: "Vendor",
      width: 150,
    },
    {
      field: "client_order_no",
      headerName: "PO No",
      sortable: true,
      filter: true,
      width: 150,
    },
    {
      field: "brand",
      headerName: "Brand",
      sortable: true,
      filter: true,
      width: 150,
    },
    {
      field: "art_name",
      headerName: "Article",
      sortable: true,
      filter: true,
      width: 300,
    },
    {
      field: "color",
      headerName: "Color",
      width: 150,
      filter: true,
      sortable: true,
    },
    {
      field: "size",
      headerName: "Size",
      width: 130,
      filter: true,
      sortable: true,
    },
    {
      field: "qty",
      headerName: "Qty",
      width: 120,
      filter: true,
      sortable: true,
    },
    {
      field: "price",
      headerName: "Price",
      width: 120,
      filter: true,
      sortable: true,
    },
    {
      field: "amount",
      headerName: "Amount",
      width: 150,
      filter: true,
      sortable: true,
    },
    {
      filter: true,
      field: "ship_type",
      headerName: "Shipping Type",
      minWidth: 150,
      flex: 1,
    },
    {
      filter: true,
      field: "ship_no",
      headerName: "Shipping No",
      minWidth: 150,
      flex: 1,
    },
    {
      filter: true,
      field: "ship_date",
      headerName: "Shipping Date",
      minWidth: 150,
      flex: 1,
      valueFormatter: (params: any) =>
        params.value == "" ? " " : dayjs(params.value).format("DD-MM-YYYY"),
    },
    {
      filter: true,
      field: "approved_ship_date",
      headerName: "Approved ShipDate",
      minWidth: 170,
      flex: 1,
      valueFormatter: (params: any) =>
        params.value == "" ? " " : dayjs(params.value).format("DD-MM-YYYY"),
    },
    {
      field: "remarks",
      headerName: "Remarks",
      minWidth: 150,
      wrapText: true,
      autoHeight: true,
      flex: 1,
    },
  ];

  const getShippingList = async () => {
    if (user_type === "Admin" && !selectedClient) {
      setAlert({
        open: true,
        message: "Please select a client",
        serverity: "error",
      });
      return;
    }
    setLoading(true);
    try {
      const response =
        user_type === "Admin"
          ? await getShipDataByClient(
              selectedClient?.id,
              dayjs(fromDate).format("YYYY-MM-DD"),
              dayjs(toDate).format("YYYY-MM-DD")
            )
          : await getShipDataByClient(
              client_id,
              dayjs(fromDate).format("YYYY-MM-DD"),
              dayjs(toDate).format("YYYY-MM-DD")
            );
      setList(response.data);
      getTotalRowCount(response.data);
    } catch (error) {
      setAlert({
        open: true,
        message: "Error fetching data",
        serverity: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const getTotalRowCount = useCallback(
    (data: any[]) => {
      setPinnedRows([]);
      const totalRow = {
        size: "Total",
        qty: data.reduce((acc, item) => acc + Number(item.qty), 0),
        amount: data.reduce((acc, item) => acc + Number(item.amount), 0),
        ship_date: "",
      };
      setPinnedRows([totalRow]);
    },
    [pinnedRows]
  );

  const onFilterChanged = useCallback(() => {
    if (!gridRef.current) return;
    const visibleRows: any[] = [];
    gridRef.current.forEachNodeAfterFilterAndSort((node: any) => {
      if (!node.group) {
        visibleRows.push(node.data);
      }
    });
    getTotalRowCount(visibleRows);
  }, [getTotalRowCount]);

  const onGridReady = (params: any) => {
    gridRef.current = params.api;
  };

  useEffect(() => {
    const getClients = async () => {
      try {
        const response: any = await getClientForSelection();
        setClients(response.data);
      } catch (error) {
        console.log(error);
      }
    };
    getClients();
  }, []);

  const handleExportToExcel = () => {
    const blob = ExportToExcel(list, "Shipping List", [
      "id",
      "client_id",
      "client",
      "vendor_id",
    ]);
    download(
      blob,
      "wip_report.xlsx",
      "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
    );
  };

  return (
    <>
      <Box
        sx={{
          mt: 1,
          p: 2,
          display: "flex",
          flexDirection: { xs: "column", sm: "row" },
          justifyContent: "flex-start",
          gap: 1,
          border: "1px solid lightgray",
          width: {
            xs: "93vw",
            lg: `calc(95vw - ${util.sidebarWidth}px)`,
          },
          borderRadius: "4px",
        }}
      >
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            label="From Date"
            format="DD-MM-YYYY"
            value={fromDate}
            onChange={(newValue) => {
              setFromDate(dayjs(newValue));
            }}
          />
          <DatePicker
            label="To Date"
            format="DD-MM-YYYY"
            value={toDate}
            onChange={(newValue) => {
              setToDate(dayjs(newValue));
            }}
          />
          <Autocomplete
            sx={{
              width: { xs: "100%", sm: 300 },
              display: user_type === "Admin" ? "block" : "none",
            }}
            options={clients}
            getOptionLabel={(option: any) => option.name}
            renderInput={(params) => <TextField {...params} label="Client" />}
            value={selectedClient}
            onChange={(_, newValue) => {
              setSelectedClient(newValue);
            }}
          />
        </LocalizationProvider>
        <Button variant="outlined" color="primary" onClick={getShippingList}>
          Refresh
        </Button>
        <Button
          variant="outlined"
          color="success"
          onClick={handleExportToExcel}
          sx={{ display: list.length > 0 ? "block" : "none" }}
        >
          Export to Excel
        </Button>
      </Box>
      <Box
        sx={{
          width: {
            // xs: `calc(100vw - ${util.erpGridMarginX}px)`,
            // lg: `calc(100vw - ${util.sidebarWidth}px - ${util.erpGridMarginX}px)`,
          },

          mt: 2,
          "& .order-id:hover": {
            textDecoration: "underline",
            color: (theme) => theme.palette.primary.main,
          },
          "& .ag-cell": {
            whiteSpace: "normal !important",
            lineHeight: "1.4 !important",
          },
        }}
      >
        <ERPGrid
          ref={gridRef}
          gridHeight={"70vh"}
          columnDefs={colDefs}
          rowData={list}
          heading={"Shipping List"}
          pinnedBottomRowData={pinnedRows}
          onGridReady={onGridReady}
          onFilterChanged={onFilterChanged}
          getRowStyle={(params: any) => {
            if (params.node?.rowPinned) {
              return { fontWeight: "bold", backgroundColor: "#d3d3d3" };
            } else if (
              params.data?.account_title?.toString().includes("Total")
            ) {
              return { fontWeight: "bold", backgroundColor: "#d3d3d3" };
            }
            return null;
          }}
        />
      </Box>
      <ERPSnakBar
        open={alert.open}
        message={alert.message}
        serverity={alert.serverity}
        onClose={() => setAlert({ open: false, message: "", serverity: "" })}
      />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </>
  );
}

export default ShippingList;
